import { useState, useMemo, ReactNode } from 'react';
import { View, Text, Pressable, TextInput, Image } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useRouter } from 'expo-router';
import { Search, Bell, Inbox, Menu, X, ChevronRight } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { webDesign, isWeb, noOutlineStyle, webStyle } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { useProfileStore } from '@/lib/state/profile-store';
import { useIsAdmin } from '@/lib/auth/role';
import { useAuth } from '@/lib/supabase';
import { useSubmissions } from '@/lib/supabase/submissions';
import { WebSidebar } from './WebSidebar';
import NotificationDropdown from '@/components/NotificationDropdown';
import { Breadcrumb } from '@/components/AppLayout';
import { ModeBadge } from '@/components/ModeIndicator';
import { mockAdminNotifications } from '@/lib/admin/mock-data';
import { mode } from '@/lib/config/app-mode';

interface WebAppShellProps {
  children: ReactNode;
  title?: string;
  showSearch?: boolean;
  headerRight?: ReactNode;
  breadcrumbs?: Breadcrumb[];
}

/**
 * Web Application Shell - Main layout wrapper for desktop web
 * Includes sidebar navigation and top header
 */
export function WebAppShell({
  children,
  title,
  showSearch = true,
  headerRight,
  breadcrumbs,
}: WebAppShellProps) {
  const router = useRouter();
  const colors = useThemeColors();
  const { isDesktop, isTablet, isMobile } = useResponsive();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);

  // Get user profile info - for investors, personal name is in user object
  const personaRole = useProfileStore((s) => s.userRole);
  const user = useProfileStore((s) => s.user);
  // Server-resolved authority, not the store cache — see WebSidebar for why.
  const { user: authUser } = useAuth();
  const { isAdmin } = useIsAdmin(authUser?.id);
  const userRole = isAdmin ? 'admin' : personaRole;
  const startupProfile = useProfileStore((s) => s.startupProfile);

  // Submission inbox count for investors
  const isInvestor = userRole === 'investor';
  const { data: submissions = [] } = useSubmissions(isInvestor ? user?.id : undefined);
  const newSubmissionCount = useMemo(
    () => submissions.filter((s) => s.status === 'pending' || s.status === 'scoring' || s.status === 'scored').length,
    [submissions],
  );
  // For startups: use user.name (person) or startupProfile.name (company)
  // For investors: use user.name only (personal name not stored in investorProfile)
  const profileName = user?.name || (userRole === 'admin' ? 'Admin' : userRole === 'startup' ? startupProfile?.name : 'Investor');
  const userRoleLabel = userRole === 'admin'
    ? 'Administrator'
    : userRole === 'startup'
    ? (user?.role || 'Founder')
    : 'Investor';

  // On mobile/tablet, render children directly (use native navigation)
  if (!isWeb || isMobile) {
    return <>{children}</>;
  }

  const sidebarWidth = sidebarCollapsed
    ? webDesign.sidebarCollapsedWidth
    : webDesign.sidebarWidth;

  // Tablet: Use overlay sidebar
  if (isTablet) {
    return (
      <View className="flex-1 flex-row" style={{ backgroundColor: colors.surface.secondary }}>
        {/* Overlay Sidebar for Tablet */}
        {mobileMenuOpen && (
          <>
            <Pressable
              className="absolute inset-0 z-40"
              style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
              onPress={() => setMobileMenuOpen(false)}
            />
            <View className="absolute left-0 top-0 bottom-0 z-50">
              <WebSidebar />
            </View>
          </>
        )}

        {/* Main Content */}
        <View className="flex-1">
          {/* Header */}
          <View
            className="flex-row items-center px-6 gap-4"
            style={{
              height: webDesign.headerHeight,
              backgroundColor: colors.surface.primary,
              borderBottomWidth: 1,
              borderBottomColor: colors.border.light,
            }}
          >
            <Pressable
              onPress={() => setMobileMenuOpen(true)}
              className="w-10 h-10 rounded-lg items-center justify-center"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <Menu size={20} color={colors.text.primary} />
            </Pressable>

            {/* Breadcrumbs or Page Title for Tablet */}
            {breadcrumbs && breadcrumbs.length > 0 ? (
              <View className="flex-row items-center gap-1 flex-1">
                {breadcrumbs.map((crumb, index) => (
                  <View key={index} className="flex-row items-center">
                    {crumb.href ? (
                      <AnimatedPressable
                        onPress={() => router.push(crumb.href!)}
                      >
                        <Text
                          style={{ color: colors.text.tertiary }}
                          className="text-sm font-medium"
                        >
                          {crumb.label}
                        </Text>
                      </AnimatedPressable>
                    ) : (
                      <Text
                        style={{ color: colors.text.primary }}
                        className="text-sm font-semibold"
                      >
                        {crumb.label}
                      </Text>
                    )}
                    {index < breadcrumbs.length - 1 && (
                      <ChevronRight
                        size={14}
                        color={colors.text.tertiary}
                        style={{ marginHorizontal: 2 }}
                      />
                    )}
                  </View>
                ))}
              </View>
            ) : title ? (
              <>
                <Text
                  style={{ color: colors.text.primary }}
                  className="text-xl font-bold"
                >
                  {title}
                </Text>
                <View className="flex-1" />
              </>
            ) : (
              <View className="flex-1" />
            )}

            {/* Submission Inbox (investor only, tablet) */}
            {isInvestor && (
              <AnimatedPressable
                onPress={() => router.push('/(investor)/submissions')}
                accessibilityRole="button"
                accessibilityLabel={`Submission inbox${newSubmissionCount > 0 ? `, ${newSubmissionCount} new` : ''}`}
                className="w-10 h-10 rounded-full items-center justify-center relative"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <Inbox size={20} color={colors.text.secondary} />
                {newSubmissionCount > 0 && (
                  <View
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.primary[600] }}
                    pointerEvents="none"
                  >
                    <Text className="text-xs font-bold text-white">
                      {newSubmissionCount > 99 ? '99' : newSubmissionCount}
                    </Text>
                  </View>
                )}
              </AnimatedPressable>
            )}

            {/* Mode indicator badge */}
            <ModeBadge />
            {headerRight}
          </View>

          {/* Content */}
          <View className="flex-1">{children}</View>
        </View>
      </View>
    );
  }

  // Desktop: Full sidebar layout
  return (
    <View className="flex-1 flex-row" style={{ backgroundColor: colors.surface.secondary }}>
      {/* Sidebar */}
      <WebSidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content Area */}
      <View className="flex-1">
        {/* Top Header */}
        <View
          className="flex-row items-center px-8 gap-6"
          style={{
            height: webDesign.headerHeight,
            backgroundColor: colors.surface.primary,
            borderBottomWidth: 1,
            borderBottomColor: colors.border.light,
          }}
        >
          {/* Breadcrumbs or Page Title */}
          {breadcrumbs && breadcrumbs.length > 0 ? (
            <View className="flex-row items-center gap-1">
              {breadcrumbs.map((crumb, index) => (
                <View key={index} className="flex-row items-center">
                  {crumb.href ? (
                    <AnimatedPressable
                      onPress={() => router.push(crumb.href!)}
                    >
                      <Text
                        style={{ color: colors.text.tertiary }}
                        className="text-base font-medium"
                      >
                        {crumb.label}
                      </Text>
                    </AnimatedPressable>
                  ) : (
                    <Text
                      style={{ color: colors.text.primary }}
                      className="text-base font-semibold"
                    >
                      {crumb.label}
                    </Text>
                  )}
                  {index < breadcrumbs.length - 1 && (
                    <ChevronRight
                      size={16}
                      color={colors.text.tertiary}
                      style={{ marginHorizontal: 4 }}
                    />
                  )}
                </View>
              ))}
            </View>
          ) : title ? (
            <Text
              style={{ color: colors.text.primary }}
              className="text-xl font-bold"
            >
              {title}
            </Text>
          ) : null}

          <View className="flex-1" />

          {/* Search Bar */}
          {showSearch && (
            <View
              className="flex-row items-center gap-2 px-4 rounded-xl"
              style={{
                backgroundColor: colors.surface.secondary,
                height: 44,
                width: 320,
              }}
            >
              <Search size={18} color={colors.text.tertiary} />
              <TextInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search..."
                placeholderTextColor={colors.text.tertiary}
                className="flex-1 text-sm"
                style={noOutlineStyle({ color: colors.text.primary })}
              />
              {searchQuery && (
                <Pressable onPress={() => setSearchQuery('')}>
                  <X size={16} color={colors.text.tertiary} />
                </Pressable>
              )}
            </View>
          )}

          {/* Submission Inbox (investor only) */}
          {isInvestor && (
            <AnimatedPressable
              onPress={() => router.push('/(investor)/submissions')}
              accessibilityRole="button"
              accessibilityLabel={`Submission inbox${newSubmissionCount > 0 ? `, ${newSubmissionCount} new` : ''}`}
              className="w-10 h-10 rounded-full items-center justify-center relative"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <Inbox size={20} color={colors.text.secondary} />
              {newSubmissionCount > 0 && (
                <View
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.primary[600] }}
                  pointerEvents="none"
                >
                  <Text className="text-xs font-bold text-white">
                    {newSubmissionCount > 99 ? '99' : newSubmissionCount}
                  </Text>
                </View>
              )}
            </AnimatedPressable>
          )}

          {/* Notifications */}
          <AnimatedPressable
            onPress={() => setNotificationDropdownOpen((v) => !v)}
            className="w-10 h-10 rounded-full items-center justify-center relative"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <Bell size={20} color={colors.text.secondary} />
            {(() => {
              const count = mode.showSampleData
                ? (userRole === 'admin'
                  ? mockAdminNotifications.filter((n) => !n.read).length
                  : 2)
                : 0;
              return count > 0 ? (
                <View
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.traffic.red }}
                  pointerEvents="none"
                >
                  <Text className="text-xs font-bold text-white">{count}</Text>
                </View>
              ) : null;
            })()}
          </AnimatedPressable>

          {/* User Avatar - clickable to user profile */}
          {profileName && (
            <AnimatedPressable
              onPress={() => router.push(userRole === 'admin' ? '/settings' : userRole === 'investor' ? '/(investor)/investorprofile' : '/(startup)/userprofile')}
              className="flex-row items-center gap-2 px-3 py-1.5 rounded-full"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              {user?.avatarUrl ? (
                <View
                  className="w-9 h-9 rounded-full overflow-hidden"
                  style={{ borderWidth: 2, borderColor: colors.primary[500] }}
                >
                  <Image
                    source={{ uri: user.avatarUrl }}
                    style={{ width: '100%', height: '100%' }}
                    resizeMode="cover"
                  />
                </View>
              ) : (
                <View
                  className="w-9 h-9 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.primary[100] }}
                >
                  <Text
                    style={{ color: colors.primary[600] }}
                    className="font-semibold text-sm"
                  >
                    {profileName.charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}
              <View className="pr-1">
                <Text
                  style={{ color: colors.text.primary }}
                  className="text-sm font-medium"
                  numberOfLines={1}
                >
                  {profileName.split(' ')[0]}
                </Text>
                <Text
                  style={{ color: colors.text.tertiary }}
                  className="text-xs capitalize"
                >
                  {userRoleLabel}
                </Text>
              </View>
            </AnimatedPressable>
          )}

          {/* Mode indicator badge */}
          <ModeBadge />

          {/* Additional Header Content */}
          {headerRight}
        </View>

        {/* Page Content */}
        <View className="flex-1">{children}</View>

        {/* Notification Dropdown — rendered outside header to avoid containment issues */}
        <NotificationDropdown
          visible={notificationDropdownOpen}
          onClose={() => setNotificationDropdownOpen(false)}
          anchorPosition={{ top: webDesign.headerHeight, right: 20 }}
        />
      </View>
    </View>
  );
}

/**
 * Web Content Container - Provides max-width and padding for content
 */
export function WebContentContainer({
  children,
  maxWidth = webDesign.contentMaxWidth,
}: {
  children: ReactNode;
  maxWidth?: number;
}) {
  return (
    <View
      className="flex-1 px-8 py-6"
      style={{ maxWidth, alignSelf: 'flex-start', width: '100%' }}
    >
      {children}
    </View>
  );
}

/**
 * Web Card - Elevated card with shadow for web
 */
export function WebCard({
  children,
  className = '',
  padding = true,
}: {
  children: ReactNode;
  className?: string;
  padding?: boolean;
}) {
  const colors = useThemeColors();
  return (
    <View
      className={`rounded-2xl ${padding ? 'p-6' : ''} ${className}`}
      style={{
        backgroundColor: colors.surface.primary,
        // @ts-ignore - web only
        boxShadow: isWeb ? webDesign.shadows.md : undefined,
      }}
    >
      {children}
    </View>
  );
}

/**
 * Web Grid - Responsive grid layout for web
 */
export function WebGrid({
  children,
  columns = { mobile: 1, tablet: 2, desktop: 3 },
  gap = 24,
}: {
  children: ReactNode;
  columns?: { mobile: number; tablet: number; desktop: number };
  gap?: number;
}) {
  const { isMobile, isTablet, isDesktop } = useResponsive();

  const colCount = isMobile
    ? columns.mobile
    : isTablet
    ? columns.tablet
    : columns.desktop;

  if (!isWeb) {
    return <View className="gap-4">{children}</View>;
  }

  return (
    <View
      style={webStyle({}, {
        display: 'grid',
        gridTemplateColumns: `repeat(${colCount}, 1fr)`,
        gap,
      })}
    >
      {children}
    </View>
  );
}
